/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { Fragment } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import { EuiButton } from '@elastic/eui'

import { useConfig } from '@modules/cui/ConfigContext'
import { useEbtContext } from '@modules/ebt/EbtContext'
import useEbtAutoOps from '@modules/autoops-lib/useEbtAutoOps'
import externalNavigate from '@modules/routing/externalNavigate'
import PermissionsGate from '@modules/permissions-components/PermissionsGate'

const messages = defineMessages({
  buttonText: {
    id: 'project.auto-ops',
    defaultMessage: 'View in AutoOps',
  },
})

type ViewInAutoOpsButtonProps = { href: string; projectId: string; disabled?: boolean }

const Button = ({ href, projectId, disabled }: ViewInAutoOpsButtonProps) => {
  const { analytics } = useEbtContext()
  const { reportEbtEvent } = useEbtAutoOps()
  const { formatMessage } = useIntl()

  return (
    <EuiButton
      color='primary'
      fill={true}
      onClick={(e) => {
        if (analytics) {
          e.preventDefault()
          reportEbtEvent(`autoOpsButtonClicked`, { resource: projectId })
          analytics.flush().then(() => externalNavigate(href, { replace: true }))
        }
      }}
      href={href}
      isDisabled={disabled}
      data-test-id='serverless-project-launch-auto-ops'
    >
      {formatMessage(messages.buttonText)}
    </EuiButton>
  )
}

const UserConsoleButton = ({ href, projectId, disabled }: ViewInAutoOpsButtonProps) => (
  <PermissionsGate
    permissions={[
      {
        type: 'autoops-project',
        action: 'get-search-ai-lake-details',
        id: projectId,
      },
    ]}
  >
    <Button href={href} projectId={projectId} disabled={disabled} />
  </PermissionsGate>
)

export const ViewInAutoOpsButton = ({
  href,
  projectId,
  disabled = false,
}: ViewInAutoOpsButtonProps) => {
  const appName = useConfig('APP_NAME')

  return (
    <Fragment>
      {appName === 'userconsole' && (
        <UserConsoleButton href={href} projectId={projectId} disabled={disabled} />
      )}
      {appName === 'adminconsole' && (
        <Button href={href} projectId={projectId} disabled={disabled} />
      )}
    </Fragment>
  )
}
